import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SITE_INFO } from '../config/constants';
import './Footer.css';

const countries = [
  'Turkey', 'Uzbekistan', 'Egypt', 'Saudi Arabia', 'United Arab Emirates',
  'Qatar', 'Kuwait', 'Oman', 'Jordan', 'Lebanon', 'Iraq', 'Iran', 'Azerbaijan',
  'Kazakhstan', 'Kyrgyzstan', 'Turkmenistan', 'Tajikistan', 'Afghanistan', 'Pakistan',
  'Germany', 'France', 'United Kingdom', 'Italy', 'Spain', 'Netherlands',
  'Belgium', 'Switzerland', 'Austria', 'Russia', 'India'
];

const primaryLinks = [
  { to: '/about', key: 'menu.about' },
  { to: '/conference-chairs', key: 'menu.products' },
  { to: '/projects', key: 'menu.projects' },
  { to: '/structure-acoustic', key: 'menu.structureAcoustic' },
];

const secondaryLinks = [
  { to: '/technical-articles', key: 'menu.technicalArticles' },
  { to: '/references', key: 'menu.references' },
  { to: '/design-your-hall', key: 'menu.designYourHall' },
  { to: '/contact', key: 'menu.contact' },
];

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { address, phone, email } = SITE_INFO.contact;

  return (
    <footer className="site-footer">
      {/* Countries marquee */}
      <div className="footer-countries">
        <div className="countries-label">{t('footer.countriesLabel')}</div>
        <div className="countries-track-wrapper">
          <div className="countries-track">
            {[...countries, ...countries].map((country, index) => (
              <span key={index} className="country-item">
                {country}
                <span className="country-separator" aria-hidden="true">·</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="footer-main">
        <div className="footer-inner">
          <div className="footer-brand">
            <Link to="/" className="footer-logo" aria-label={t('footer.title')}>
              <img src="/logonish.jpeg" alt="" width={132} height={62} />
            </Link>
            <p className="footer-description">{t('footer.description')}</p>
          </div>

          <nav className="footer-nav" aria-label={t('footer.quickLinks')}>
            <h4 className="footer-heading">{t('footer.quickLinks')}</h4>
            <ul className="footer-links">
              {primaryLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to}>{t(link.key)}</Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav className="footer-nav" aria-label={t('footer.explore')}>
            <h4 className="footer-heading">{t('footer.explore')}</h4>
            <ul className="footer-links">
              {secondaryLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to}>{t(link.key)}</Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="footer-contact">
            <h4 className="footer-heading">{t('footer.contact')}</h4>
            <ul className="footer-contact-list">
              <li>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z" />
                </svg>
                <span>{address}</span>
              </li>
              {phone.map((item) => (
                <li key={item.number}>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24c1.12.37 2.32.57 3.57.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2Z" />
                  </svg>
                  <a href={`tel:${item.number.replace(/\s/g, '')}`}>
                    {item.number}
                    <span className="footer-contact-note">
                      {t(`contact.phoneLabels.${item.label}`)}
                    </span>
                  </a>
                </li>
              ))}
              {email.map((address) => (
                <li key={address}>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 4-8 5-8-5V6l8 5 8-5v2Z" />
                  </svg>
                  <a href={`mailto:${address}`}>{address}</a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Legal bar */}
      <div className="footer-legal">
        <div className="footer-legal-inner">
          <p>
            © {new Date().getFullYear()} {t('footer.title')}. {t('footer.copyright')}
          </p>
          <Link to="/contact" className="footer-legal-cta">
            {t('common.getQuote')}
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
